import { useLocation, useParams, Link, useNavigate } from "../router";
import "./productDetail.css";
import { useEffect } from "react";
import scroll from "../utils/scroll";

export const ServiceDetail = ({ services = [] }) => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fallback = services.find((s) => s.slug === slug);
  const service = location.state?.item || fallback;

  useEffect(() => {
    document.body.classList.add("detail-page-body");
    const menu = document.getElementById("menu");
    if (menu) {
      menu.classList.add("detail-page-nav");
    }
    return () => {
      document.body.classList.remove("detail-page-body");
      if (menu) {
        menu.classList.remove("detail-page-nav");
      }
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const attemptScroll = (attempt = 0) => {
      const target = document.getElementById(sectionId);
      if (target) {
        scroll.animateScroll(target, { offset: 58 });
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, document.title, "/");
        }
      } else if (attempt < 20) {
        requestAnimationFrame(() => attemptScroll(attempt + 1));
      }
    };
    requestAnimationFrame(() => attemptScroll());
  };

  const handleContact = (e) => {
    e.preventDefault();
    navigate("/");
    setTimeout(() => scrollToSection("contact"), 30);
  };

  const detailContent = {
    "cloud-architecture-support": {
      image: "/img/services/cloud_architecture_support.png",
      paragraphs: [
        "Cloud Architecture & Support at Nopsa Ventures is designed to help organizations build, scale, and operate secure cloud environments with confidence. We partner with you from strategy to execution—ensuring your cloud foundation is resilient, cost-efficient, and future-ready.",
        "We design and support cloud architectures across leading platforms including AWS, Microsoft Azure, Google Cloud Platform (GCP) and Github — tailored to your business goals and regulatory needs.",
        "Our approach combines best-practice reference architectures with real-world operational experience, helping teams move fast without compromising security or reliability.",
        <strong>Core Capabilities</strong>,
        "• Cloud Strategy & Architecture: readiness assessments, migration planning, multi-cloud and hybrid design, Well-Architected solutions.",
        "• Landing Zones & Foundations: secure landing zones with governance, network topology, IAM, identity federation and access control.",
        "• Security, Compliance & Resilience: zero-trust patterns, compliance-ready architectures (SOC 2, ISO, HIPAA, PCI-DSS), HA/DR strategies.",
        "• Cost & Performance Optimization: FinOps guardrails, right-sizing, autoscaling, performance tuning, usage visibility.",
        "• Ongoing Cloud Support: architecture reviews, continuous improvement, incident response, runbooks, and knowledge transfer.",
        "Why Nopsa Ventures? Vendor-agnostic expertise, security-first design from day one, scalable patterns that grow with your teams, and clear documentation that empowers your engineers.",
        "Whether launching new platforms, modernizing legacy systems, or stabilizing existing environments, we provide the architecture and support to keep your cloud running smoothly.",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "automated-security-scanning": {
      image:
        "/img/services/automated_security_scanning.png",
      paragraphs: [
        "Build fast without sacrificing security by embedding automated security scanning directly into your development and delivery pipelines.",
        "Our Automated Security Scanning service integrates seamlessly into your CI/CD workflows to continuously identify, prioritize, and remediate risks across your entire software supply chain—from source code to production workloads.",
        <strong>What We Secure</strong>,
        "• Application Code: Static and dynamic analysis (SAST & DAST) to detect vulnerabilities early, before they reach production.",
        "• Open Source Dependencies: Software Composition Analysis (SCA) to uncover known vulnerabilities, license risks, and supply-chain threats in third-party libraries.",
        "• Containers & Images: Deep scanning of container images and registries to prevent vulnerable or untrusted artifacts from being deployed.",
        "• Infrastructure & Runtime: Policy enforcement and security controls aligned with cloud and container orchestration environments.",
        <strong>How It Works</strong>,
        "• Shift-Left Security: Automated scans trigger on every commit, pull request, and build—giving developers immediate, actionable feedback.",
        "• Policy-Driven Gates: Enforce security thresholds with automated approval or blocking rules to prevent risky deployments.",
        "• Trusted Artifact Delivery: Image signing and verification ensure only approved, tamper-free artifacts are promoted across environments.",
        "• Actionable Insights, Not Noise: Findings are contextualized and routed to the right code owners, reducing alert fatigue and accelerating remediation.",
        <strong>The Outcome</strong>, 
        "Faster, safer releases; reduced attack surface and supply-chain risk; improved developer productivity; continuous compliance without slowing innovation.",
        "Security becomes an automated guardrail, not a bottleneck.",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "devops-ci-cd-gitops": {
      image:
        "/img/services/devops_cicd_gitops.png",
      paragraphs: [
        <strong>Production-grade GitOps pipelines built for speed, safety, and scale.</strong>,
        "We design and implement GitOps-driven CI/CD pipelines that promote applications and infrastructure seamlessly from development to production, with every change fully versioned, auditable, and approved. Git becomes the single source of truth—so deployments are predictable, repeatable, and reversible.",
        <strong>What you get</strong>,
        "• GitOps-first delivery pipelines:",
        "  Automated promotion across environments (dev → staging → prod)",
        "  Built-in change history, pull-request reviews, and approval gates",
        "  Fast rollbacks and drift detection using declarative state",
        "• Golden images & reusable delivery templates:",
        "  Standardized “golden” base images for security and consistency",
        "  Reusable templates for application and infrastructure delivery",
        "  Works across AWS, Azure, GCP, and hybrid environments",
        "  Opinionated best practices without locking you into a single toolchain",
        "• Observability baked into every deployment:",
        "  Traces, logs, and metrics wired in from day one",
        "  Deployment events automatically correlated with runtime behavior",
        "  Faster root-cause analysis when something changes—or breaks",
        "  Clear visibility into how each release impacts performance and reliability",
        <strong>The result</strong>,
        "• Faster, safer releases",
        "• Fewer production surprises",
        "• Consistent delivery across teams and clouds",
        "• A DevOps platform that scales with your business—not against it",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "containers-orchestration": {
      image:
        "/img/services/containers.png",
      paragraphs: [
        "We design and secure production-grade container platforms that scale with your business—without turning operations or security into a bottleneck. Our approach blends architecture, automation, governance, and GitOps so teams can move fast and stay in control.",
        <strong>End-to-End Container Platform Design</strong>,
        "We build container platforms as cohesive systems, not fragmented tools. Each layer is intentionally designed and integrated:",
        "• Registry layer – Private, secure image registries with provenance, versioning, signing, and promotion workflows across environments.",
        "• Runtime layer – Kubernetes and container runtimes tuned for reliability, performance, and cost efficiency.",
        "• Ingress & networking – Traffic management, zero-trust access, service mesh integration, and resilient ingress patterns.",
        "• GitOps & deployment automation – Declarative, Git-driven delivery using Argo CD and Flux for Kubernetes-native continuous deployment, environment consistency, drift detection, and safe rollbacks.",
        "• Policy & governance – Guardrails baked in using policy-as-code for security, compliance, and operational consistency.",
        "The result: a platform that empowers developers while giving platform and security teams confidence.",
        <strong>Low-Risk Monolith → Microservices Migration</strong>,
        "Modernization doesn’t have to be a “big bang.”",
        "We deliver migration playbooks that de-risk the journey from monoliths to containers:",
        "• Incremental strangler-pattern migrations",
        "• Clear service boundaries and dependency mapping",
        "• Parallel run and rollback strategies",
        "• Observability-first validation to catch issues early",
        "This enables gradual adoption of microservices with minimal disruption to customers and revenue.",
        <strong>Container Supply Chain Security</strong>,
        "Security starts long before runtime. We secure the entire container supply chain:",
        "• Image signing and verification",
        "• Dependency and SBOM management",
        "• Vulnerability scanning and continuous compliance",
        "• Hardened base images and minimal OS footprints",
        "• Runtime security controls and anomaly detection",
        "From source to production, every artifact is trusted, traceable, and policy-enforced.",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "secrets-management": {
      image:
        "/img/services/secrets_management.png",
      paragraphs: [
        "Nopsa Ventures designs, builds, and operates production-grade secrets management platforms that scale with your infrastructure and security requirements—without slowing down developers.",
        <strong>What we deliver</strong>,
        "Secure Secret Backends, Done Right",
        "We architect and run hardened secret backends using Vault, cloud KMS, and Key Vault–style systems, tailored to your environment (cloud, hybrid, or on-prem).",
        "• Strong isolation between teams and workloads",
        "• Least-privilege access models with short-lived credentials",
        "• Audit logging, compliance alignment, and disaster-recovery design",
        "• High availability and performance under real production load",
        <strong>Automated, Zero-Touch Rotation</strong>,
        "Eliminate static credentials and manual rotations. We implement fully automated rotation pipelines for:",
        "• Application and service-to-service credentials",
        "• Database users and roles",
        "• TLS certificates (internal and public)",
        "Rotation happens continuously and safely—without outages, redeploys, or human intervention.",
        <strong>Secret Injection Without Code Changes</strong>,
        "We enable secure secret delivery across modern and legacy platforms without requiring application rewrites:",
        "• Containers & Kubernetes (sidecars, init containers, native integrations)",
        "• Serverless runtimes (ephemeral credentials at invocation time)",
        "• Virtual machines and legacy services",
        "Secrets never land in source control, container images, or plaintext config files—only in memory, only when needed.",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "ibm-hashicorp-support": {
      image:
        "/img/services/hashicorp_support.png",
      paragraphs: [
        "Nopsa Ventures provides end-to-end support for organizations running HashiCorp tooling in production—helping you move from working configs to a secure, scalable, and operable platform your teams can trust.",
        <strong>Platform implementation & deep tuning</strong>,
        "We design, implement, and optimize your HashiCorp stack across Terraform, Vault, Consul, Nomad, Packer, and Boundary, aligned to your cloud and operating model. This includes:",
        "• Production-grade architectures (HA, DR, and multi-environment layouts)",
        "• Performance and reliability tuning for real workloads",
        "• Secure identity, secrets, and networking patterns baked in from day one",
        "• Clean upgrade paths to keep pace with upstream releases",
        <strong>Opinionated module & workspace standards</strong>,
        "We establish clear, reusable standards that make the right thing the easy thing:",
        "• Terraform module conventions that enforce security, tagging, and cost controls",
        "• Workspace and environment layouts that scale across teams and accounts",
        "• Policy guardrails (naming, ownership, compliance) without slowing delivery",
        "• Consistent patterns that reduce drift and review fatigue",
        <strong>Runbooks, enablement & safe self-service</strong>,
        "Technology alone isn’t enough—we make sure your teams can operate it confidently:",
        "• Practical runbooks for day-to-day operations and incident response",
        "• Hands-on training tailored to platform and application teams",
        "• Clear self-service workflows with built-in guardrails",
        "• Knowledge transfer so you’re not dependent on outside experts",
        <strong>The outcome</strong>,
        "A HashiCorp platform that is:",
        "• Secure by default",
        "• Predictable to operate",
        "• Easy for teams to self-serve",
        "• Ready to scale with your business",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "intelligent-cloud-automation": {
      image:
        "/img/services/intelligent_cloud_automation.png",
      paragraphs: [
        <strong>Event-driven automation to heal, scale, and patch without manual intervention.</strong>,
        "Cloud environments continuously emit signals—metrics, logs, events, and security findings. Intelligent automation consumes these signals in real time and translates them into precise actions. When services degrade, capacity thresholds are crossed, or vulnerabilities are detected, remediation workflows are triggered automatically. Systems self-heal by restarting or replacing failed components, scale elastically to meet demand, and apply patches safely using progressive rollouts. The result is faster recovery, reduced mean time to resolution, and fewer production incidents—without relying on human intervention or brittle runbooks.",
        <strong>Policy engines that evaluate risk signals and trigger just-in-time controls.</strong>,
        "Policies codify intent across security, reliability, and compliance. These engines continuously evaluate contextual risk signals such as identity posture, configuration drift, workload behavior, and threat intelligence. When risk changes, controls adapt in real time—tightening access, enforcing guardrails, isolating workloads, or requiring additional verification only when needed. This just-in-time enforcement replaces static controls with dynamic, risk-aware decisions that reduce blast radius while preserving developer velocity and operational flexibility.",
        <strong>Dashboards that surface drift, anomalies, and optimization opportunities.</strong>,
        "Unified dashboards provide a real-time operational view across cloud accounts, regions, and services. They highlight configuration drift from desired state, detect anomalous behavior before it escalates into incidents, and reveal inefficiencies across performance, cost, and resource utilization. Instead of raw telemetry, teams get prioritized insights and trends that support proactive decision-making. This visibility enables continuous improvement—helping organizations strengthen resilience, improve security posture, and optimize cloud spend over time.",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "identity-and-access-management": {
      image:
        "/img/services/identity_access_management.png",
      paragraphs: [
        <strong>Identity-first security that scales with your cloud</strong>,
        "Nopsa Ventures’ Identity and Access Management service is built around a simple principle: every request is authenticated, authorized, and continuously verified. We design and operate modern IAM foundations that reduce risk, accelerate engineering teams, and support multi-cloud growth—without compromising security.",
        <strong>Identity-first architecture by design</strong>,
        "We implement centralized identity as the control plane for your infrastructure, platforms, and applications. With Single Sign-On (SSO), Multi-Factor Authentication (MFA), and least-privilege role models, every user and workload receives precisely the access it needs—nothing more, nothing less—across AWS, Azure, GCP, Kubernetes, and SaaS platforms. This eliminates credential sprawl and creates a consistent, auditable access experience.",
        <strong>JWT-based federated access for Kubernetes workloads</strong>,
        "Static credentials and long-lived secrets are one of the biggest risks in cloud-native environments. We replace them with JWT-based federated access for Kubernetes containers and workloads. Using OIDC federation, workloads authenticate using short-lived JWTs issued by a trusted identity provider. Kubernetes service accounts, pods, and controllers assume cloud and platform permissions dynamically—without embedding secrets in code, images, or configuration. Access is workload-identity–driven, automatically rotated, and tightly scoped to runtime context.",
        <strong>This approach enables:</strong>,
        "• Secure pod-to-cloud and pod-to-API access",
        "• Zero secret distribution inside containers",
        "• Strong isolation between workloads and environments",
        "• Full auditability of workload identity usage",
        <strong>Granular policy enforcement everywhere</strong>,
        "From cloud consoles to APIs, Kubernetes clusters, and internal services, we enforce fine-grained policies that dramatically reduce blast radius. Access decisions are evaluated based on identity, workload, role, context, and risk signals—ensuring that compromised credentials or containers cannot cascade into platform-wide incidents.",
        <strong>Outcome</strong>,
        "A resilient, zero-trust–aligned IAM foundation that strengthens security posture, simplifies cloud-native operations, and enables teams to move fast—without opening doors they shouldn’t.",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
    "certificate-authority": {
      image:
        "/img/services/certificate_authority.png",
      paragraphs: [
        "Nopsa Ventures delivers a production-grade Certificate Authority as a Service designed for modern cloud-native and hybrid environments. We help teams move beyond ad-hoc TLS management to a secure, automated, and observable PKI foundation.",
        <strong>PKI design & managed issuance</strong>,
        "We design and operate a scalable Public Key Infrastructure tailored to your organization’s trust boundaries and risk model. Using Vault-backed PKI and ACME-compatible authorities, we enable secure certificate issuance for humans, services, and machines—without manual key handling or brittle scripts.",
        "• Clear separation of root, intermediate, and workload CAs",
        "• Short-lived certificates aligned with zero-trust principles",
        "• Policy-driven issuance and revocation",
        <strong>Fully automated renewals</strong>,
        "Certificates should never expire silently. We integrate automated issuance and renewal directly into your platform stack:",
        "• Services & internal apps – seamless mTLS and HTTPS for microservices",
        "• Ingress controllers – automatic TLS for Kubernetes and edge routing",
        "• CI/CD & workloads – certificates issued just-in-time, rotated continuously",
        "This removes operational burden while dramatically reducing the blast radius of leaked or compromised credentials.",
        <strong>Observability, alerts & compliance</strong>,
        "We make certificate health visible and actionable:",
        "• Dashboards showing certificate age, trust chains, and usage",
        "• Proactive alerts for expiring, misconfigured, or non-compliant certificates",
        "• Audit-friendly logs for issuance, renewal, and revocation events",
        "The result is fewer outages, faster incident response, and stronger compliance with internal security standards and external regulations.",
        <strong>Outcome</strong>,
        "A secure, automated, and observable certificate lifecycle—so your teams can focus on building systems, not babysitting TLS.",
        <a href="/" onClick={handleContact}>If you're interested in learning more about this service, please, contact us directly →</a>
      ],
    },
  };

  if (!service) {
    return (
      <div className="detail-page empty-state">
        <div className="detail-card">
          <p>We couldn't find that service.</p>
          <Link to="/" className="detail-cta">← Back to home</Link>
        </div>
      </div>
    );
  }

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/");
    setTimeout(() => scrollToSection("services"), 30);
  };

  const detail = detailContent[service.slug] || {};

  return (
    <div className="detail-page">
      <div className="detail-card">
        <div className="detail-header">
          <div className="pill secondary">
            <i className={service.icon}></i>
            <span>Service</span>
          </div>
          <a href="/" className="detail-cta" onClick={handleBack}>← Back to Services</a>
        </div>
        {detail.image && (
          <div className="detail-image">
            <img src={detail.image} alt={service.name} />
          </div>
        )}
        {Array.isArray(detail.paragraphs) &&
          detail.paragraphs.map((p, idx) => <p key={idx}>{p}</p>)}
      </div>
    </div>
  );
};

export default ServiceDetail;
